/* eslint-disable i18next/no-literal-string */
import { AutocompleteRenderInputParams, AutocompleteRenderOptionState, TextField } from "@mui/material";
import Div from "components/primitives/Div";
import ThemeProvider2024 from "components/MuiThemeProvider/ThemeProvider2024";
import VirtualAutocomplete from "components/VirtualAutocomplete";
import Autocomplete from "components/Inputs/Autocomplete";
import { useCallback, useState } from "react";
import useAthleteStore from "providers/AthletesProvider/useAthleteStore";
import { Athlete } from "types";
import AthleteAvatar from "components/AthleteAvatar";
import AthleteHelpers from "helpers/AthleteHelpers";
import VelocityTargetStatusChip from "components/VelocityTargetStatusChip";
import type { VelocityTargetStatus } from "components/VelocityTargetStatusChip";

const VELOCITY_TARGET_STATUSES: VelocityTargetStatus[] = [
  "OnTarget",
  "BelowTarget",
  "AboveTarget",
  "Inconsistent",
  "Exceeded",
];

function SandBox() {
  const [selectedOption, setSelectedOption] = useState<Athlete | null>(null);

  const athletes = useAthleteStore();

  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => <TextField {...params} label="Select an option" variant="outlined" />,
    []
  );

  const renderOption = useCallback((option: Athlete) => {
    return (
      <Div sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <AthleteAvatar athleteId={option.id} />
        <span>{AthleteHelpers.getFullName(option)}</span>
      </Div>
    );
  }, []);

  const renderAutocompleteOption = useCallback(
    (
      props: React.HTMLAttributes<HTMLLIElement> & { key: React.Key },
      option: Athlete,
      _state: AutocompleteRenderOptionState
    ) => (
      <li {...props} key={option.id}>
        {renderOption(option)}
      </li>
    ),
    [renderOption]
  );

  const groupBy = useCallback((option: Athlete) => option.firstName, []);

  const onChange = useCallback((_event: React.SyntheticEvent, newValue: Athlete | null) => {
    setSelectedOption(newValue);
  }, []);

  const getOptionLabel = useCallback((option: Athlete) => AthleteHelpers.getFullName(option), []);

  return (
    <ThemeProvider2024>
      <Div sx={{ display: "flex", gap: 2, padding: 2 }}>
        <Div sx={{ width: "400px", display: "flex", flexDirection: "column", gap: 1 }}>
          <p>Virtual Autocomplete</p>
          <VirtualAutocomplete
            options={athletes}
            value={selectedOption}
            onChange={onChange}
            renderInput={renderInput}
            renderOptionContent={renderOption}
            groupBy={groupBy}
            getOptionLabel={getOptionLabel}
          />
        </Div>
        <Div sx={{ width: "400px", display: "flex", flexDirection: "column", gap: 1 }}>
          <p>Standard Autocomplete</p>
          <Autocomplete
            options={athletes}
            value={selectedOption}
            onChange={onChange}
            renderOption={renderAutocompleteOption}
            groupBy={groupBy}
            getOptionLabel={getOptionLabel}
          />
        </Div>
        <Div sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1 }}>
          <p>Velocity target status chips</p>
          {VELOCITY_TARGET_STATUSES.map((status) => (
            <VelocityTargetStatusChip key={status} status={status} />
          ))}
        </Div>
      </Div>
    </ThemeProvider2024>
  );
}

export default SandBox;
